import { getFunctions, httpsCallable } from 'firebase/functions';
import { getDatabase, ref, get } from 'firebase/database';

const functions = getFunctions();

export interface UserWithCache {
  uid: string;
  email: string;
  displayName: string;
  createdAt: string;
  lastSignIn: string;
  migratedAt: string | null;
  profileGrade: string;
  derivedGrade: string;
  gradeDifference: boolean;
  courseCount: number;
  hasGrade12Courses: boolean;
  hasGrade11Courses: boolean;
  programCount: number;
  cacheKeys: string[];
  needsRefresh: boolean;
}

export interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalUsers: number;
  limit: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface SummaryInfo {
  totalUsersWithCache: number;
  needsRefreshCount: number;
  processedCount: number;
}

export interface UsersWithCacheResult {
  users: UserWithCache[];
  pagination: PaginationInfo;
  summary: SummaryInfo;
}

export interface ClearCacheResult {
  success: boolean;
  message: string;
  userInfo: {
    uid: string;
    email: string;
    displayName: string;
  };
  recalculationInfo: {
    programCount: number;
    recalculatedAt: string;
    hadExistingCache: boolean;
    cacheCleared: boolean;
    newGradeLogicApplied: boolean;
  };
}

export interface UserDetailsResult {
  success: boolean;
  userInfo: {
    uid: string;
    email: string;
    displayName: string;
    createdAt: string;
    lastSignIn: string;
  };
  profileData: {
    profileGrade: string;
    derivedGrade: string;
    gradeDifference: boolean;
    hasProfileData: boolean;
  };
  courseData: {
    courseCount: number;
    courses: string[];
    hasGrade12Courses: boolean;
    hasGrade11Courses: boolean;
  };
  eligibilityCache: {
    hasCache: boolean;
    programCount: number;
    cacheKeys: string[];
    error?: string;
  };
  dataStatus: {
    hasData: boolean;
    reasons: string[];
    details: {
      hasProfile: boolean;
      hasCourses: boolean;
      hasGrades: boolean;
      courseCount: number;
      gradeCount: number;
    };
  };
}

export interface EligibilityResult {
  ifEligible: boolean | null;
  isOnRightPath: string;
  alertMessage: string;
  nextYearSuggestion?: string;
  calculatedAverage?: number;
  programAverage?: number;
  missingCourses?: string[];
  gradeIssues?: string[];
  coursesUsedInAverage?: string[];
  coursesWithGradeIssues?: string[];
}

class EligibilityCacheService {
  /**
   * Get users with eligibility cache
   */
  async getUsersWithCache(page: number = 1, limit: number = 20): Promise<UsersWithCacheResult> {
    const getUsersWithCache = httpsCallable(functions, 'adminGetUsersWithEligibilityCache');
    
    const result = await getUsersWithCache({
      page,
      limit,
    });
    
    return result.data as UsersWithCacheResult;
  }

  /**
   * Clear cache and recalculate for a specific user
   */
  async clearCacheAndRecalculate(targetUserId: string): Promise<ClearCacheResult> {
    const clearCacheAndRecalculate = httpsCallable(functions, 'adminClearCacheAndRecalculate');
    
    const result = await clearCacheAndRecalculate({
      targetUserId,
    });
    
    return result.data as ClearCacheResult;
  }

  /**
   * Get detailed user information
   */
  async getUserDetails(targetUserId: string): Promise<UserDetailsResult> {
    const getUserDetails = httpsCallable(functions, 'adminGetUserDetails');
    
    const result = await getUserDetails({
      targetUserId,
    });
    
    return result.data as UserDetailsResult;
  }

  /**
   * Compare user eligibility before and after recalculation
   */
  async compareUserEligibility(targetUserId: string): Promise<any> {
    const compareUserEligibility = httpsCallable(functions, 'adminCompareUserEligibility');
    
    const result = await compareUserEligibility({
      targetUserId,
    });
    
    return result.data;
  }

  /**
   * Get program eligibility from Firebase cache for a specific user and program
   */
  async getProgramEligibilityFromFirebase(userId: string, programId: string): Promise<EligibilityResult | null> {
    try {
      const db = getDatabase();
      const cacheRef = ref(db, `eligibilityCache/${userId}/${programId}`);
      const snapshot = await get(cacheRef);
      
      if (snapshot.exists()) {
        return snapshot.val() as EligibilityResult;
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching program eligibility from Firebase cache:', error);
      return null;
    }
  }
}

export const eligibilityCacheService = new EligibilityCacheService();

// Export the function directly for backward compatibility
export const getProgramEligibilityFromFirebase = eligibilityCacheService.getProgramEligibilityFromFirebase.bind(eligibilityCacheService);